import { ViewStyle, TextStyle } from 'react-native';

/**
 * Calendar Settings "Select Calendar" dropdown visual tokens.
 * Prefer AppSelect for new UI; reuse these helpers for any remaining MUI Selects.
 */

export type SelectPalette = {
  inputBg: string;
  inputBorder: string;
  focusBorder: string;
  textPrimary: string;
  textSecondary: string;
  placeholder: string;
  menuBg: string;
  menuText: string;
  selectedBg: string;
  selectedText: string;
  check: string;
  divider: string;
  errorBorder: string;
};

export const SELECT_RADIUS = 12;
/** Closed trigger min height — use minHeight (not fixed height) so label glyphs are not clipped. */
export const SELECT_TRIGGER_HEIGHT = 50;
export const SELECT_COMPACT_HEIGHT = 36;

/** Sentinel for empty string options (Tamagui Select dislikes ""). */
export const APP_SELECT_EMPTY_VALUE = '__app_select_empty__';

export function getSelectPalette(theme: boolean): SelectPalette {
  return {
    inputBg: theme ? '#ffffff' : '#1e293b',
    inputBorder: theme ? '#e2e8f0' : '#6b7280',
    focusBorder: theme ? '#3b82f6' : '#2563eb',
    textPrimary: theme ? '#0f172a' : '#f3f4f6',
    textSecondary: theme ? '#64748b' : '#94a3b8',
    placeholder: theme ? '#64748b' : '#94a3b8',
    menuBg: theme ? '#ffffff' : '#1e293b',
    menuText: theme ? '#0f172a' : '#f3f4f6',
    selectedBg: theme ? '#eff6ff' : '#1e3a8a',
    selectedText: theme ? '#0369a1' : '#60a5fa',
    check: theme ? '#0369a1' : '#60a5fa',
    divider: theme ? '#e5e7eb' : '#4b5563',
    errorBorder: '#ef4444',
  };
}

export function getSelectWrapStyle(
  palette: SelectPalette,
  options?: { hasError?: boolean; compact?: boolean; minWidth?: number; open?: boolean },
): ViewStyle {
  return {
    // Allow minWidth: 0 so full-width selects (e.g. Easy Mode product form) are not clipped.
    minWidth: options?.minWidth ?? 0,
    maxWidth: '100%',
    borderWidth: 1,
    borderRadius: SELECT_RADIUS,
    borderColor: options?.hasError
      ? palette.errorBorder
      : options?.open
        ? palette.focusBorder
        : palette.inputBorder,
    backgroundColor: palette.inputBg,
    paddingHorizontal: options?.compact ? 10 : 16,
    paddingVertical: 0,
    minHeight: options?.compact ? SELECT_COMPACT_HEIGHT : SELECT_TRIGGER_HEIGHT,
    overflow: 'visible',
  };
}

export function getSelectTriggerStyle(
  palette: SelectPalette,
  options?: { compact?: boolean },
): ViewStyle {
  return {
    backgroundColor: 'transparent',
    borderWidth: 0,
    minHeight: options?.compact ? SELECT_COMPACT_HEIGHT : SELECT_TRIGGER_HEIGHT,
    paddingHorizontal: 0,
    paddingVertical: 0,
    justifyContent: 'center',
  };
}

export function getSelectValueStyle(
  palette: SelectPalette,
  options?: { hasValue?: boolean; fontSize?: number },
): TextStyle {
  return {
    fontSize: options?.fontSize ?? 15,
    fontWeight: options?.hasValue ? '600' : '400',
    color: options?.hasValue ? palette.textPrimary : palette.placeholder,
  };
}

export function getSelectMenuPaperStyle(palette: SelectPalette): ViewStyle {
  return {
    maxHeight: 300,
    borderRadius: SELECT_RADIUS,
    backgroundColor: palette.menuBg,
    borderWidth: 1,
    borderColor: palette.inputBorder,
    overflow: 'hidden',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.15,
    shadowRadius: 12,
    elevation: 4,
  };
}

export function encodeSelectValue(value: string | null | undefined): string {
  if (value == null || value === '') return APP_SELECT_EMPTY_VALUE;
  return value;
}

export function decodeSelectValue(value: string): string {
  if (value === APP_SELECT_EMPTY_VALUE) return '';
  return value;
}
